/**
 * CLASSIFICATION DES NOTES PAR LE MODÈLE ACADÉMIQUE EFFECTIF DU TENANT (écart M7-P0-5).
 *
 * Mentions, lettres, points de GPA et seuil de réussite ne sont PLUS écrits dans le code : ils se
 * lisent du modèle du tenant (`tenant.modele_academique`, rendu par `modele_academique_effectif()` :
 * le modèle choisi par le tenant, sinon le gabarit de repli lu sans écriture). Une bande de mention
 * est une donnée : `{ code, min_ratio, libelle: {fr, en}, lettre?, points?, couleur? }`, avec un
 * seuil RELATIF à l'échelle (`grading_system.max`) — la même bande sert une échelle de 20, de 4,
 * de 10 ou de 100.
 *
 * Sans modèle (non chargé) ou sans bande : on ne devine rien, on rend `null` / chaîne vide.
 * Fonctions pures, zéro dépendance React ni réseau.
 */

const EPS = 1e-9;

/** Texte d'un objet `{fr, en, …}` dans la langue demandée, avec repli. */
export function texteLocalise(obj, langue = 'fr') {
  if (!obj || typeof obj !== 'object') return typeof obj === 'string' ? obj : '';
  return obj[langue] || obj.fr || obj.en || Object.values(obj)[0] || '';
}

/** Échelle maximale du modèle, ou null si elle n'est pas numérique et positive. */
export function echelleMax(modele) {
  const max = Number(modele?.grading_system?.max);
  return Number.isFinite(max) && max > 0 ? max : null;
}

/** Note de passage du modèle (échelle du tenant), ou null. */
export function notePassage(modele) {
  const v = modele?.grading_system?.pass;
  const n = Number(v);
  return v !== null && v !== undefined && v !== '' && Number.isFinite(n) ? n : null;
}

/**
 * Seuil de réussite du tenant : celui de son modèle effectif (`seuil_reussite` = la politique d'accès qu'il
 * a réglée, sinon sa note de passage). Aucune valeur par défaut écrite ici (null si le modèle n'en porte pas).
 */
export function seuilReussite(modele) {
  const brut = modele?.seuil_reussite;
  const n = Number(brut);
  if (brut !== null && brut !== undefined && brut !== '' && Number.isFinite(n) && n > 0) return n;
  return notePassage(modele);
}

/** Bandes du modèle, de la plus haute à la plus basse. */
export function bandesDuModele(modele) {
  const bandes = Array.isArray(modele?.mentions) ? modele.mentions : [];
  return bandes
    .filter((b) => b && Number.isFinite(Number(b.min_ratio)))
    .slice()
    .sort((a, b) => Number(b.min_ratio) - Number(a.min_ratio));
}

/** Bande atteinte par une note (échelle du tenant), ou null (note absente, modèle sans échelle ou sans bande). */
export function bandeDe(note, modele) {
  if (note === null || note === undefined || note === '') return null;
  const n = Number(note);
  const max = echelleMax(modele);
  if (!Number.isFinite(n) || max === null) return null;
  const bandes = bandesDuModele(modele);
  return bandes.find((b) => n >= Number(b.min_ratio) * max - EPS) || null;
}

/**
 * Classe une note : `{ bande, mention, lettre, points, couleur }`, ou null.
 * `lettre` = la lettre de la bande, à défaut son libellé ; `points` = null si la bande n'en porte
 * pas (jamais inventé) ; `couleur` = celle de la bande, sinon rouge sous la note de passage.
 */
export function classerNote(note, modele, langue = 'fr') {
  const bande = bandeDe(note, modele);
  if (!bande) return null;
  const mention = texteLocalise(bande.libelle, langue);
  const passage = notePassage(modele);
  const sousLePassage = passage !== null && Number(note) < passage;
  return {
    bande,
    mention,
    lettre: bande.lettre ?? mention,
    points: Number.isFinite(Number(bande.points)) && bande.points !== null ? Number(bande.points) : null,
    couleur: bande.couleur || (sousLePassage ? 'var(--color-danger)' : 'var(--color-primary)'),
  };
}

/** Libellé de la mention dans la langue demandée ('' si la note n'est pas classable). */
export function mentionDe(note, modele, langue = 'fr') {
  return classerNote(note, modele, langue)?.mention || '';
}

/** Libellé bilingue d'une mention (« Bien / Good ») : une seule fois si les deux langues disent la même chose. */
export function mentionBilingue(note, modele, langues = ['fr', 'en']) {
  const bande = bandeDe(note, modele);
  if (!bande) return '';
  const textes = langues.map((l) => bande.libelle?.[l]).filter(Boolean);
  return [...new Set(textes)].join(' / ') || texteLocalise(bande.libelle);
}

/** Points de GPA de la bande atteinte, ou null. */
export function pointsDe(note, modele) {
  return classerNote(note, modele)?.points ?? null;
}

/**
 * Répartition d'une liste de moyennes selon les bandes du modèle, en pourcentage entier, SOUS LA MÊME
 * FORME que `repartition_mentions` de `get_home_snapshot` (`{ code, libelle, min_ratio, pct }`, de la
 * bande la plus haute à la plus basse). Liste vide ou modèle sans bande : [].
 */
export function repartitionMentions(moyennes, modele) {
  const valeurs = (moyennes || []).filter((m) => m !== null && m !== undefined && Number.isFinite(Number(m)));
  const bandes = bandesDuModele(modele);
  if (!valeurs.length || !bandes.length) return [];
  const comptes = new Map(bandes.map((b) => [b.code, 0]));
  valeurs.forEach((m) => {
    const b = bandeDe(m, modele);
    if (b) comptes.set(b.code, (comptes.get(b.code) || 0) + 1);
  });
  return bandes.map((b) => ({
    code: b.code,
    libelle: b.libelle,
    min_ratio: Number(b.min_ratio),
    pct: Math.round((comptes.get(b.code) || 0) * 100 / valeurs.length),
  }));
}
